import React, { useEffect, useRef, useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import esriConfig from "@arcgis/core/config";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import WebMap from "@arcgis/core/WebMap";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";

import { intersects, overlaps } from "@arcgis/core/geometry/geometryEngine";

import { ensureArcgisAuth } from "../../../../../utils/arcgisAuth";

// Widgets
import Sketch from "@arcgis/core/widgets/Sketch";
import SketchTooltipOptions from "@arcgis/core/views/interactive/sketch/SketchTooltipOptions";
import Search from "@arcgis/core/widgets/Search";

const WebMapArcgis = ({ project }) => {
  const mapRef = useRef();

  let view = useRef(null);

  const [projectCoordinates, setProjectCoordinates] = useState([
    117.017156, 5.489928,
  ]);

  useEffect(() => {
    // Skip until the project has loaded — querying the geo server with
    // `project_id = undefined` is rejected with a 400.
    if (!project?.id) return;

    // Auth is now brokered server-side — see src/utils/arcgisAuth.js. Replaces
    // the previous OAuth popup which consumed an ArcGIS named-user seat per
    // SMEPPS user.

    const rendererArea = {
      type: "unique-value",
      field: "OBJECTID", // Replace with the actual field name in your data
      defaultSymbol: {
        type: "simple-fill",
        color: [239, 187, 8, 0.5],
        outline: {
          width: 1,
          color: [239, 187, 8],
        },
      },
      uniqueValueInfos: [],
    };

    const popupProjectInfo = {
      title: "Maklumat Projek",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "name",
              label: "Nama Projek",
              isEditable: true,
              tooltip: "",
              visible: true,
              format: null,
              stringFieldOption: "text-box",
            },
            {
              fieldName: "centroid_longitude",
              label: "Longitud",
              isEditable: true,
              tooltip: "",
              visible: true,
              format: null,
              stringFieldOption: "text-box",
            },
            {
              fieldName: "centroid_latitude",
              label: "Latitud",
              isEditable: true,
              tooltip: "",
              visible: true,
              format: null,
              stringFieldOption: "text-box",
            },
          ],
        },
      ],
    };

    const projectLabel = {
      symbol: {
        type: "text",
        color: "#eceff1",
        haloColor: "#37474f",
        haloSize: "1px",
        font: {
          size: "12px",
          family: "Noto Sans",
          style: "italic",
          weight: "normal",
        },
      },

      labelPlacement: "above-center",
      labelExpressionInfo: {
        expression: "$feature.name",
      },
    };

    const graphicsLayer = new GraphicsLayer();

    ensureArcgisAuth()
      .then(() => {
        const districtLayer = new FeatureLayer({
          url: "https://services7.arcgis.com/imUjhC2BWfjR1bCA/arcgis/rest/services/SbhDistrictBdy/FeatureServer/0",
        });

        const areaLayer = new FeatureLayer({
          url: "https://smepps-geo.sabah.gov.my/arcmanager/rest/services/Project_Location_Area/FeatureServer/3",
          outFields: ["*"],
          renderer: rendererArea,
          popupTemplate: popupProjectInfo,
          labelingInfo: [projectLabel],
          definitionExpression: `project_id = ${project.id}`,
        });

        const webMap = new WebMap({
          basemap: "hybrid", // Replace with your WebMap ID
          layers: [districtLayer, graphicsLayer, areaLayer],
        });

        const fetchProjectCoordinates = async () => {
          try {
            const query = areaLayer.createQuery();
            query.where = `project_id = ${project.id}`; // Adjust as necessary
            query.outFields = ["centroid_latitude", "centroid_longitude"]; // Replace with your actual field names

            const results = await areaLayer.queryFeatures(query);

            if (results.features.length > 0) {
              const firstFeature = results.features[0];
              const newCoordinates = [
                firstFeature.attributes.centroid_longitude,
                firstFeature.attributes.centroid_latitude,
              ];

              view = new MapView({
                container: mapRef.current,
                map: webMap,
                center: newCoordinates,
                zoom: 19,
              });

              const sketchTooltipOptions = new SketchTooltipOptions({
                enabled: true,
                visibleElements: {
                  area: true,
                  length: true,
                },
              });

              view.when().then(() => {
                view.whenLayerView(areaLayer).then((layerView) => {
                  if (layerView) {
                    const processedSet = new Set();

                    layerView.watch("updating", (updating) => {
                      if (!updating) {
                        layerView.queryFeatures().then((res) => {
                          res.features.forEach((feature, index) => {
                            res.features.forEach((otherFeature, otherIndex) => {
                              if (index !== otherIndex) {
                                const isOverlapping = overlaps(
                                  feature.geometry,
                                  otherFeature.geometry,
                                );

                                if (isOverlapping) {
                                  const attributes = otherFeature.attributes;
                                  const objectId = attributes.OBJECTID;

                                  if (!processedSet.has(objectId)) {
                                    const newUniqueValueInfo = {
                                      value: objectId.toString(),
                                      symbol: {
                                        type: "picture-fill",
                                        // url: getSymbolUrlBasedOnLayerCount(
                                        //   processedSet.size + 1,
                                        // ),
                                        width: "24px",
                                        height: "24px",
                                        outline: {
                                          style: "solid",
                                        },
                                      },
                                    };

                                    rendererArea.uniqueValueInfos.push(
                                      newUniqueValueInfo,
                                    );
                                    processedSet.add(objectId);
                                  }
                                }
                              }
                            });
                          });

                          // Ensure the layer is re-rendered after symbols are updated
                          areaLayer.renderer = rendererArea;
                        });
                      }
                    });
                  } else {
                    console.error("Layer view not available.");
                  }
                });
              });

              const searchWidget = new Search({
                view: view,
                allPlaceholder: "Cari",
                includeDefaultSources: true,
                sources: [
                  {
                    layer: districtLayer,
                    searchFields: ["Dist_Name"],
                    displayField: "Dist_Name",
                    exactMatch: false,
                  },
                ],
              });

              view.ui.add(searchWidget, {
                position: "top-right",
                index: 2,
              });

              // Store the last added graphic
              let lastGraphic = null;

              // Function to clear the last graphic
              function clearLastGraphic() {
                if (lastGraphic) {
                  view.graphics.remove(lastGraphic);
                  lastGraphic = null;
                }
              }

              searchWidget.on("search-complete", function (event) {
                if (event.numResults === 0) {
                  // Check if the search term is a coordinate
                  var searchTerm = searchWidget.searchTerm.trim();
                  var coords = searchTerm.split(",");
                  if (coords.length === 2) {
                    var lat = parseFloat(coords[0]);
                    var lon = parseFloat(coords[1]);

                    if (!isNaN(lat) && !isNaN(lon)) {
                      var point = new Point({
                        longitude: lon,
                        latitude: lat,
                      });

                      // Create a graphic for the point
                      var graphic = new Graphic({
                        geometry: point,
                        symbol: {
                          type: "simple-marker",
                          color: "red",
                          size: "12px",
                        },
                        popupTemplate: {
                          title: "Coordinates",
                          content: `
                            <ul>
                              <li><b>Latitude:</b> ${lat}</li>
                              <li><b>Longitude:</b> ${lon}</li>
                            </ul>
                          `,
                        },
                      });

                      // view.graphics.removeAll();
                      // view.graphics.add(graphic);
                      clearLastGraphic(); // Clear the last graphic if there is one
                      view.graphics.add(graphic);
                      lastGraphic = graphic; // Store the current graphic

                      view
                        .goTo({
                          target: point,
                          zoom: 15, // Adjust zoom level as needed
                        })
                        .catch(function (error) {
                          console.error(error);
                        });

                      view.popup.open({
                        features: [graphic],
                        location: point,
                      });

                      // Manually trigger the selection
                      searchWidget.searchTerm = searchTerm;
                      searchWidget.resultGraphic = graphic;
                      searchWidget.viewModel.selectedResult = {
                        feature: graphic,
                        name: "Coordinates: " + searchTerm,
                      };
                    }
                  }
                }
              });

              // Remove the point when clicking on the map, but not on the point.
              // Restrict the hit test to the search marker in `view.graphics`:
              // an unrestricted hitTest also runs the FeatureLayer's internal 2D
              // hit test, which crashed on polygon clicks ("r is not a function").
              view.on("click", function (event) {
                if (!lastGraphic) return;

                view
                  .hitTest(event, { include: view.graphics })
                  .then(function (response) {
                    const isGraphic = response.results.some(function (result) {
                      return result.graphic === lastGraphic;
                    });
                    if (!isGraphic) {
                      clearLastGraphic();
                    }
                  })
                  .catch(function (error) {
                    console.error("Error hit-testing search marker:", error);
                  });
              });
            } else {
              console.warn("No features found for the given project ID.");
            }
          } catch (error) {
            console.error("Error fetching project coordinates: ", error);
          }
        };

        fetchProjectCoordinates();
      })
      .catch((error) => {
        console.error("Authentication failed: ", error);
      });

    return () => {
      if (view.current) {
        view.container = null;
      }
    };
  }, [project]);

  return (
    <React.Fragment>
      <div
        ref={mapRef}
        style={{ height: "100%", width: "100%", padding: 0, margin: 0 }}
      />
    </React.Fragment>
  );
};

export default WebMapArcgis;
